import { Box, VStack, Flex, Text, Divider, Link } from "@chakra-ui/layout";
import { Alert, AlertDescription, AlertIcon, Checkbox } from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import { PaymentStatus } from "doxecure-commons/doxecureTypes";
import { $isfunction, $ok } from "foundation-ts/commons";
import { TSError } from "foundation-ts/tserrors";
import { Resp } from "foundation-ts/tsrequest";
import { Nullable } from "foundation-ts/types";

import { G1Button } from "@components/G1Button";
import { MethodTab } from "@components/Payment/MethodTab";
import { MollieCard } from "@/components/Payment/MollieCard";
import { MandateCard } from "@/components/Payment/MandateCard";
import { MollieInstance, PaymentFormRef, PaymentMethodType } from "@components/Payment/types";
import { config } from "@/config/config";
import { BankIcon, CardIcon } from "@/utils/icons";

interface ConfirmationStepProps {
    paymentStatus: Nullable<PaymentStatus>;
    selectedPaymentMethod: Nullable<PaymentMethodType>;
    companyName: string;
    testMode: boolean;
    expiredTrialPeriod: Nullable<string>;
    initializeMollie: () => MollieInstance;
    onMethodChange: (id: PaymentMethodType) => void;
    onCardSubmit: (token: string, captchaToken: string) => Promise<void>;
    onMandateSubmit: (consumerAccount: string, consumerName: string, captchaToken: string) => Promise<void>;
}

export default function ConfirmationStep(props: ConfirmationStepProps) {
    const {
        paymentStatus,
        selectedPaymentMethod,
        companyName,
        testMode,
        expiredTrialPeriod,
        initializeMollie,
        onMethodChange,
        onCardSubmit,
        onMandateSubmit,
    } = props;

    const [paymentFormValid, setPaymentFormValid] = useState(false);
    const [cguAccepted, setCguAccepted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const paymentFormRef = useRef<PaymentFormRef>(null);

    const isSubmitDisabled = !paymentFormValid || !cguAccepted || loading;
    const paymentStatusText = paymentStatus === "canceled" ? "a été annulé" : (paymentStatus === "expired" ? "a expiré" : (paymentStatus === "failed" ? "a échoué" : ""));

    const handleSubmit = useCallback(async () => {
        if (!$isfunction(executeRecaptcha)) {
            console.warn("executeRecaptcha non disponible");
            return
        }

        if (isSubmitDisabled) { return; }
        try {
            setLoading(true);
            setFormError(null);
            const captchaToken = await executeRecaptcha?.("register");
            if (!$ok(captchaToken)) {
                setFormError("La vérification reCAPTCHA a échoué, veuillez réessayer.");
                return;
            }
            await paymentFormRef.current?.submit(captchaToken!);
        } catch (e) {
            let message = "Une erreur inattendue est survenue";
            if (e instanceof TSError) {
                switch (e.errorCode) {
                    case Resp.Conflict:
                        message = "Une organisation avec le même identifiant ou un utilisateur avec le même email existe déjà";
                        break;
                    case Resp.BadRequest:
                        message = "L'identifiant de l'organisation ou le numéro de TVA est invalide";
                        break;
                    case -1: // Tokenisation mollie
                        message = e.message;
                        break;
                    default:
                        break;
                }
            }
            setFormError(message);
        } finally {
            setLoading(false);
        }
    }, [executeRecaptcha, isSubmitDisabled]);

    useEffect(() => {
        setPaymentFormValid(false);
        setFormError(null);
        setCguAccepted(false);
    }, [selectedPaymentMethod]);

    return (
        <VStack spacing={6} align="stretch">
            { paymentStatusText !== "" &&
                <Box p={4} bg="red.600" borderColor="red.300" borderRadius="md">
                    <Text fontSize="md" color="gray.100" lineHeight="1.7">Le paiement a {paymentStatusText}, veuillez recommencer.</Text>
                </Box>
            }
            { paymentStatus !== "paid" &&
                <Flex gap={3}>
                    <MethodTab
                        selected={selectedPaymentMethod === "sepa"}
                        icon={<BankIcon />}
                        label="Virement SEPA"
                        description="Prélèvement automatique"
                        onClick={() => { onMethodChange("sepa"); setPaymentFormValid(false); }}
                    />
                    <MethodTab
                        selected={selectedPaymentMethod === "card"}
                        icon={<CardIcon />}
                        label="Carte bancaire"
                        description="CB, Visa, Mastercard"
                        onClick={() => { onMethodChange("card"); setPaymentFormValid(false); }}
                    />
                </Flex>
            }

            { $ok(selectedPaymentMethod) && (paymentStatus !== "paid") && (
                <Box key={selectedPaymentMethod} p={6} borderRadius="xl" border="1px solid" borderColor="gray.600" bg="prussianBlue.500">
                    {selectedPaymentMethod === "card" && (
                        <MollieCard
                            signup
                            ref={paymentFormRef}
                            initializeMollie={initializeMollie}
                            onSubmit={onCardSubmit}
                            onValidityChange={setPaymentFormValid}
                        />
                    )}
                    {selectedPaymentMethod === "sepa" && (
                        <MandateCard
                            ref={paymentFormRef}
                            companyName={companyName}
                            testmode={testMode}
                            onSubmit={onMandateSubmit}
                            onValidityChange={setPaymentFormValid}
                        />
                    )}

                    <VStack spacing={4} align="stretch" mt={5}>
                        <Divider />
                        <Checkbox
                            isChecked={cguAccepted}
                            onChange={(e) => setCguAccepted(e.target.checked)}
                            isDisabled={!paymentFormValid}
                            colorScheme="blue"
                        >
                            <Text fontSize="sm">
                                J'accepte les{" "}
                                <Link href={config.app.cguURL} isExternal color="blue.300" textDecoration="underline">
                                    Conditions Générales d'Utilisation et de Vente
                                </Link>{" "}
                            </Text>
                        </Checkbox>

                        {formError && (
                            <Alert status="error" borderRadius="md" fontSize="sm" color="black">
                                <AlertIcon />
                                <AlertDescription>{formError}</AlertDescription>
                            </Alert>
                        )}

                        <G1Button component="connection.form" variant="important" title="Activer l'abonnement" w="full" size="md" fontSize="md"
                                  isLoading={loading} loadingText="Traitement en cours…" isDisabled={isSubmitDisabled} onClick={handleSubmit} />
                        <Text fontSize="xs" color="gray.400" textAlign="center">
                            Vos données bancaires sont transmises de manière sécurisée vers notre sous-traitant Mollie
                        </Text>
                    </VStack>
                </Box>
            )}

            { paymentStatus === "paid" && (
                <VStack p={4} bg="green.300" border="1px solid" borderColor="green.500" borderRadius="md" gap={3}>
                    <Text fontSize="lg" color="gray.700">Félicitations ! Le compte de votre organisation a été créé, le mandat choisi sera automatiquement utilisé pour vos prochains paiements.</Text>
                    <Text fontSize="lg" color="gray.700">Vous allez recevoir un mail pour activer votre accès, n'hésitez pas à le faire rapidement. Si, dans les 10 jours, vous ne l'avez toujours pas activé, alors il sera automatiquement supprimé et vous devrez recommencer votre inscription.</Text>
                    <Text fontSize="lg" color="gray.700" fontWeight="semibold">Votre période d'essai commence dès maintenant et se terminera le {expiredTrialPeriod}.</Text>
                </VStack>
            )}
        </VStack>
    );
}